import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { User, UserSearchResult } from '../../models/user.model';
import { selectAllUsers, selectSearchForm } from '../../store/selectors/users.selectors';
import * as UsersSelectors from './../../store/selectors/users.selectors';
import { clearUsers, loadUsers } from './../../store/actions/users.action';
import { Statuses } from '../../shared/constants/status.constants';
import { Plans } from '../../shared/constants/plan.constants';
import { Roles } from '../../shared/constants/role.constants';
import { selectAuthUser } from '../../store/selectors/auth.selectors';
import { selectCategoriesLoading } from '../../store/selectors/categories.selectors';
import { loadCategories } from '../../store/actions/categories.actions';
import { MapParameters } from '../../models/map-parameters.model';

@Component({
  selector: 'app-users',
  standalone: false,
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit, OnDestroy {
  users$: Observable<UserSearchResult[] | undefined>;
  loadingUsers$!: Observable<boolean>;

  searchForm: FormGroup;
  authUser: User | null = null;

  roles = Roles;

  roleOptions = [Roles.Admin, Roles.Editor];
  plans = [Plans.Flex, Plans.Pro, Plans.Unlimited];
  statuses = [Statuses.Approved, Statuses.Pending, Statuses.Blocked];

  authUser$ = this.store.select(selectAuthUser);
  loadingCategories$!: Observable<boolean>;

  mapParameters?: MapParameters;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private fb: FormBuilder
  ) {
    this.loadingCategories$ = this.store.select(selectCategoriesLoading);
    this.store.dispatch(loadCategories());

    this.loadingUsers$ = this.store.select(UsersSelectors.selectUsersLoading);
    this.users$ = this.store.select(selectAllUsers);
    this.searchForm = this.fb.group({
      name: [null],
      email: [null],
      status: [null],
      phoneNumber: [null],
      role: [null],
      plan: [null],
      northEastLat: undefined,
      northEastLng: undefined,
      southWestLat: undefined,
      southWestLng: undefined,
    });
    this.authUser$.pipe(takeUntil(this.destroy$)).subscribe((u) => {
      this.authUser = u;
    });
  }

  ngOnInit(): void {
    this.store.select(selectSearchForm);
  }

  search(): void {
    if (!(this.authUser?.role === Roles.Admin || this.authUser?.role === Roles.Editor)) {
      this.searchForm.patchValue({ email: null, phoneNumber: null, role: null, status: null, plan: null });
    }
    this.store.dispatch(loadUsers({ query: this.searchForm.value }));
  }

  reset(): void {
    this.store.dispatch(clearUsers());
    this.mapParameters = undefined;

    this.searchForm.reset({
      name: null,
      email: null,
      status: null,
      phoneNumber: null,
      role: null,
      plan: null,
    });
  }

  mapChanged(event: MapParameters) {
    this.mapParameters = event;
    this.northEastLat?.setValue(event.northEastLat);
    this.northEastLng?.setValue(event.northEastLng);
    this.southWestLat?.setValue(event.southWestLat);
    this.southWestLng?.setValue(event.southWestLng);
    this.search();
  }

  clearMapData() {
    this.northEastLat?.setValue(undefined);
    this.northEastLng?.setValue(undefined);
    this.southWestLat?.setValue(undefined);
    this.southWestLng?.setValue(undefined);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get northEastLat() {
    return this.searchForm.get('northEastLat');
  }
  get northEastLng() {
    return this.searchForm.get('northEastLng');
  }
  get southWestLat() {
    return this.searchForm.get('southWestLat');
  }
  get southWestLng() {
    return this.searchForm.get('southWestLng');
  }
}
